import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Plus,
  Trash2,
  Save,
  Clock,
  FileText,
  Repeat,
  RefreshCw,
  Loader2,
  KeyRound,
  PlugZap,
  FileSignature,
  UploadCloud,
  Pencil,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  configApi,
  credentialsApi,
  efirmaApi,
  type AppConfig,
  type TermsTemplate,
  type CredentialsFlow,
  type CredentialsMeta,
  type CredentialsStatus,
  type EfirmaMeta,
} from "@/api/config";
import { queryKeys } from "@/api/query-keys";
import { authErrorMessage } from "@/auth/errors";
import { requireRole } from "@/auth/role-guard";
import { formatTimestamp } from "@/lib/mask-helpers";
import { ApiKeySection } from "./-sections/api-key-section";

export const Route = createFileRoute("/_authenticated/configuracion/")({
  beforeLoad: requireRole(["admin"]),
  component: ConfiguracionPage,
});

function ConfiguracionPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const configQuery = useQuery({
    queryKey: queryKeys.config.all,
    queryFn: configApi.get,
  });

  const [config, setConfig] = useState<AppConfig | null>(null);

  // Sincroniza el estado editable cuando llega la configuración del backend.
  useEffect(() => {
    if (configQuery.data) setConfig(configQuery.data);
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: configApi.save,
    onSuccess: (saved) => {
      qc.setQueryData(queryKeys.config.all, saved);
      setConfig(saved);
      toast.success(t("config.page.savedToast"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  if (configQuery.isLoading || !config) {
    return (
      <>
        <PageHeader
          title={t("config.page.title")}
          subtitle={t("config.page.subtitle")}
        />
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t("config.page.loading")}
        </div>
      </>
    );
  }

  const setTermino = (i: number, patch: Partial<TermsTemplate>) =>
    setConfig((c) =>
      c
        ? {
            ...c,
            terms: c.terms.map((t, idx) => (idx === i ? { ...t, ...patch } : t)),
          }
        : c,
    );
  const addTermino = () =>
    setConfig((c) =>
      c ? { ...c, terms: [...c.terms, { contractType: "", terms: "" }] } : c,
    );
  const removeTermino = (i: number) =>
    setConfig((c) =>
      c ? { ...c, terms: c.terms.filter((_, idx) => idx !== i) } : c,
    );

  const guardar = () =>
    saveMutation.mutate({
      ...config,
      terms: config.terms.filter((t) => t.contractType.trim()),
    });

  return (
    <>
      <PageHeader
        title={t("config.page.title")}
        subtitle={t("config.page.subtitle")}
        actions={
          <Button onClick={guardar} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Save className="size-4" />
            )}
            {t("common.actions.save")}
          </Button>
        }
      />

      <div className="space-y-8 max-w-3xl">
        {/* Términos y Condiciones por tipo de contrato */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <FileText className="size-4 text-primary" />
            <h2 className="text-sm font-semibold">{t("config.page.terms.title")}</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            <Trans
              i18nKey="config.page.terms.description"
              components={{ b: <span className="font-medium" /> }}
            />
          </p>

          <div className="space-y-4">
            {config.terms.map((tpl, i) => (
              <div key={i} className="rounded-md border border-input p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    value={tpl.contractType}
                    onChange={(e) => setTermino(i, { contractType: e.target.value })}
                    placeholder={t("config.page.terms.typePlaceholder")}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <Button variant="ghost" size="sm" onClick={() => removeTermino(i)}>
                    <Trash2 className="size-4 text-muted-foreground" />
                  </Button>
                </div>
                <textarea
                  value={tpl.terms}
                  onChange={(e) => setTermino(i, { terms: e.target.value })}
                  placeholder={t("config.page.terms.textPlaceholder")}
                  className="h-28 w-full rounded-md border border-input bg-background p-2 text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addTermino}>
            <Plus className="size-4" />
            {t("config.page.terms.add")}
          </Button>
        </section>

        {/* Horario de ejecución automática */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock className="size-4 text-primary" />
            <h2 className="text-sm font-semibold">{t("config.page.schedule.title")}</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("config.page.schedule.description")}
          </p>
          <div className="flex flex-wrap items-center gap-4 rounded-md border border-input p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={config.schedule.enabled}
                onChange={(e) =>
                  setConfig((c) =>
                    c ? { ...c, schedule: { ...c.schedule, enabled: e.target.checked } } : c,
                  )
                }
              />
              {t("config.page.schedule.enable")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              {t("config.page.schedule.hour")}
              <input
                type="time"
                value={config.schedule.hour}
                onChange={(e) =>
                  setConfig((c) =>
                    c ? { ...c, schedule: { ...c.schedule, hour: e.target.value } } : c,
                  )
                }
                className="rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <span className="text-xs text-muted-foreground">
              {config.schedule.enabled
                ? t("config.page.schedule.activeAt", { hour: config.schedule.hour })
                : t("config.page.schedule.disabled")}
            </span>
          </div>
        </section>

        {/* Re-subida inteligente de inscripciones por número de contrato */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Repeat className="size-4 text-primary" />
            <h2 className="text-sm font-semibold">{t("config.page.reupload.title")}</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("config.page.reupload.description")}
          </p>
          <div className="flex flex-wrap items-center gap-4 rounded-md border border-input p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={config.reupload?.enabled ?? false}
                onChange={(e) =>
                  setConfig((c) =>
                    c
                      ? {
                          ...c,
                          reupload: {
                            ...(c.reupload ?? { enabled: false }),
                            enabled: e.target.checked,
                          },
                        }
                      : c,
                  )
                }
              />
              {t("config.page.reupload.enable")}
            </label>
            <span className="text-xs text-muted-foreground">
              {config.reupload?.enabled
                ? t("config.page.reupload.enabled")
                : t("config.page.reupload.disabled")}
            </span>
          </div>
        </section>

        {/* Reintentos */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <RefreshCw className="size-4 text-primary" />
            <h2 className="text-sm font-semibold">{t("config.page.retries.title")}</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("config.page.retries.description")}
          </p>
          <div className="grid sm:grid-cols-2 gap-4 rounded-md border border-input p-3">
            <NumField
              label={t("config.page.retries.maxAttempts")}
              value={config.retries.maxAttempts}
              min={1}
              onChange={(v) =>
                setConfig((c) =>
                  c ? { ...c, retries: { ...c.retries, maxAttempts: v } } : c,
                )
              }
            />
          </div>
        </section>

        {/* Credenciales del portal del RUG */}
        <CredentialsSection />

        {/* e.firma (FIEL) por flujo — cifrado envelope del lado cliente */}
        <EfirmaSection />

        {/* API key de la Public API (server-to-server) — solo admin */}
        <ApiKeySection />
      </div>
    </>
  );
}

const credentialsSchema = z.object({
  username: z.string().min(1, "config.credentials.errors.usernameRequired"),
  password: z.string().min(1, "config.credentials.errors.passwordRequired"),
});
type CredentialsValues = z.infer<typeof credentialsSchema>;

const LOGIN_TEST_VARIANT = {
  success: "success",
  failed: "destructive",
  pending: "warning",
} as const;

function CredentialsSection() {
  const { t } = useTranslation();

  const statusQuery = useQuery({
    queryKey: queryKeys.config.credentials.status,
    queryFn: credentialsApi.status,
    // Mientras un test-login esté en curso, refresca hasta que llegue el resultado.
    refetchInterval: (query) => {
      const data = query.state.data as CredentialsStatus | undefined;
      const pending =
        data?.inscripcion?.lastLoginTest?.status === "pending" ||
        data?.baja?.lastLoginTest?.status === "pending";
      return pending ? 3000 : false;
    },
  });

  useEffect(() => {
    if (statusQuery.isError) {
      toast.error(t("config.credentials.toasts.loadError"));
    }
  }, [statusQuery.isError, t]);

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <KeyRound className="size-4 text-primary" />
        <h2 className="text-sm font-semibold">
          {t("config.credentials.title")}
        </h2>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("config.credentials.description")}
      </p>

      {statusQuery.isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t("common.loading")}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {/* key por PRESENCIA de meta: al pasar de "sin credenciales" a "con
              credenciales" (primer guardado o recuperación tras un error de
              carga) la tarjeta se remonta y arranca en modo lectura. La key es
              estable mientras meta existe, así el polling del estado no expulsa
              al usuario del modo edición. */}
          <CredentialsFlowForm
            key={`inscripcion-${!!statusQuery.data?.inscripcion}`}
            flow="inscripcion"
            meta={statusQuery.data?.inscripcion}
          />
          <CredentialsFlowForm
            key={`baja-${!!statusQuery.data?.baja}`}
            flow="baja"
            meta={statusQuery.data?.baja}
          />
        </div>
      )}
    </section>
  );
}

function CredentialsFlowForm({
  flow,
  meta,
}: {
  flow: CredentialsFlow;
  meta?: CredentialsMeta;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  // Sin credenciales aún → abrir directamente en modo edición (mostrar inputs).
  // Con credenciales → arrancar en modo lectura; los inputs sólo aparecen al
  // pulsar "Editar". Evita inputs vacíos permanentes cuando ya hay credenciales.
  const [editing, setEditing] = useState(!meta);

  const form = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { username: "", password: "" },
  });

  const saveMutation = useMutation({
    mutationFn: (v: CredentialsValues) =>
      flow === "inscripcion"
        ? credentialsApi.setInscripcion(v.username, v.password)
        : credentialsApi.setBaja(v.username, v.password),
    onSuccess: () => {
      // El password jamás se conserva en el cliente tras enviarlo.
      form.reset({ username: "", password: "" });
      setEditing(false);
      qc.invalidateQueries({
        queryKey: queryKeys.config.credentials.status,
      });
      // El backend ya disparó el login de prueba al guardar (queda 'pending').
      toast.success(t("config.credentials.toasts.savedTesting"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const testMutation = useMutation({
    mutationFn: () => credentialsApi.testLogin(flow),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.config.credentials.status,
      });
      toast.success(t("config.credentials.toasts.testTriggered"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const onSubmit = form.handleSubmit((v) => saveMutation.mutate(v));

  const cancelEdit = () => {
    form.reset({ username: "", password: "" });
    setEditing(false);
  };

  const test = meta?.lastLoginTest;
  const statusKey = test?.status ?? "untested";
  const usernameId = `${flow}-username`;
  const passwordId = `${flow}-password`;

  return (
    <div className="rounded-md border border-input p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {t(`config.credentials.flows.${flow}`)}
        </h3>
        <Badge
          variant={test ? LOGIN_TEST_VARIANT[test.status] : "secondary"}
        >
          {t(`config.credentials.status.${statusKey}`)}
        </Badge>
      </div>

      {meta ? (
        <dl className="space-y-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <dt className="font-medium">
              {t("config.credentials.currentUser")}:
            </dt>
            <dd className="font-mono">{meta.maskedUsername}</dd>
          </div>
          <div className="flex items-center gap-1">
            <dt className="font-medium">
              {t("config.credentials.updatedAt")}:
            </dt>
            <dd>{formatTimestamp(meta.updatedAt)}</dd>
          </div>
          {test?.testedAt ? (
            <div className="flex items-center gap-1">
              <dt className="font-medium">
                {t("config.credentials.status.label")}:
              </dt>
              <dd>{formatTimestamp(test.testedAt)}</dd>
            </div>
          ) : null}
          {test?.status === "failed" && test.errorMessage ? (
            <p className="text-destructive">{test.errorMessage}</p>
          ) : null}
        </dl>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t("config.credentials.notConfigured")}
        </p>
      )}

      {editing ? (
        <form onSubmit={onSubmit} className="space-y-3" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor={usernameId}>
              {t("config.credentials.username")}
            </Label>
            <Input
              id={usernameId}
              type="text"
              autoComplete="off"
              placeholder={t("config.credentials.usernamePlaceholder")}
              {...form.register("username")}
              aria-invalid={!!form.formState.errors.username}
            />
            {form.formState.errors.username ? (
              <p className="text-xs text-destructive">
                {t(form.formState.errors.username.message ?? "")}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={passwordId}>
              {t("config.credentials.password")}
            </Label>
            <Input
              id={passwordId}
              type="password"
              autoComplete="new-password"
              placeholder={t("config.credentials.passwordPlaceholder")}
              {...form.register("password")}
              aria-invalid={!!form.formState.errors.password}
            />
            {form.formState.errors.password ? (
              <p className="text-xs text-destructive">
                {t(form.formState.errors.password.message ?? "")}
              </p>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              {saveMutation.isPending
                ? t("config.credentials.saving")
                : t("config.credentials.save")}
            </Button>
            {/* "Cancelar" sólo cuando ya existen credenciales (hay estado al que volver). */}
            {meta ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={saveMutation.isPending}
                onClick={cancelEdit}
              >
                <X className="size-4" />
                {t("config.credentials.cancel")}
              </Button>
            ) : null}
          </div>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-4" />
            {t("config.credentials.edit")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={
              !meta || testMutation.isPending || test?.status === "pending"
            }
            onClick={() => testMutation.mutate()}
          >
            {testMutation.isPending || test?.status === "pending" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <PlugZap className="size-4" />
            )}
            {testMutation.isPending || test?.status === "pending"
              ? t("config.credentials.testing")
              : t("config.credentials.retest")}
          </Button>
        </div>
      )}
    </div>
  );
}

// --- e.firma (FIEL) por flujo -------------------------------------------------

const efirmaSchema = z.object({
  // Validación de presencia sobre el FileList del input de archivo.
  cer: z
    .custom<FileList>()
    .refine((f) => !!f && f.length > 0, "config.efirma.errors.cerRequired"),
  key: z
    .custom<FileList>()
    .refine((f) => !!f && f.length > 0, "config.efirma.errors.keyRequired"),
  password: z.string().min(1, "config.efirma.errors.passwordRequired"),
});
type EfirmaValues = z.infer<typeof efirmaSchema>;

function EfirmaSection() {
  const { t } = useTranslation();

  const statusQuery = useQuery({
    queryKey: queryKeys.config.efirma.status,
    queryFn: efirmaApi.status,
  });

  useEffect(() => {
    if (statusQuery.isError) {
      toast.error(t("config.efirma.toasts.loadError"));
    }
  }, [statusQuery.isError, t]);

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <FileSignature className="size-4 text-primary" />
        <h2 className="text-sm font-semibold">{t("config.efirma.title")}</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("config.efirma.description")}
      </p>

      {statusQuery.isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t("common.loading")}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <EfirmaFlowForm
            flow="inscripcion"
            meta={statusQuery.data?.inscripcion}
          />
          <EfirmaFlowForm flow="baja" meta={statusQuery.data?.baja} />
        </div>
      )}
    </section>
  );
}

function EfirmaFlowForm({
  flow,
  meta,
}: {
  flow: CredentialsFlow;
  meta?: EfirmaMeta;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Remontar los inputs de archivo tras enviar, para no retener los archivos.
  const [resetToken, setResetToken] = useState(0);
  const uploaded = meta?.uploaded === true;
  // Modo edición: si ya hay e.firma cargada, ocultar los inputs y mostrar solo un
  // botón para reemplazarla (evita inputs vacíos permanentes). Sin cargar → editar.
  const [editing, setEditing] = useState(!uploaded);

  const form = useForm<EfirmaValues>({
    resolver: zodResolver(efirmaSchema),
    defaultValues: { password: "" },
  });

  const saveMutation = useMutation({
    mutationFn: (v: EfirmaValues) => {
      const cerFile = v.cer[0];
      const keyFile = v.key[0];
      return flow === "inscripcion"
        ? efirmaApi.setInscripcion(cerFile, keyFile, v.password)
        : efirmaApi.setBaja(cerFile, keyFile, v.password);
    },
    onSuccess: () => {
      // Ni la contraseña ni los archivos se conservan en el cliente tras enviarlos.
      form.reset({ password: "" });
      setResetToken((n) => n + 1);
      setEditing(false);
      qc.invalidateQueries({ queryKey: queryKeys.config.efirma.status });
      toast.success(t("config.efirma.toasts.saved"));
    },
    onError: (err) => {
      // Limpiar la contraseña y remontar los inputs también al fallar, para no
      // retener el texto plano de la e.firma en el DOM/estado tras un error.
      form.reset({ password: "" });
      setResetToken((n) => n + 1);
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : t("config.efirma.toasts.saveError"),
      );
    },
  });

  const onSubmit = form.handleSubmit((v) => saveMutation.mutate(v));

  const cerId = `${flow}-efirma-cer`;
  const keyId = `${flow}-efirma-key`;
  const passwordId = `${flow}-efirma-password`;

  const cerList = form.watch("cer");
  const keyList = form.watch("key");
  const cerName = cerList && cerList.length > 0 ? cerList[0].name : null;
  const keyName = keyList && keyList.length > 0 ? keyList[0].name : null;

  const cancelEdit = () => {
    form.reset({ password: "" });
    setResetToken((n) => n + 1);
    setEditing(false);
  };

  return (
    <div className="rounded-md border border-input p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {t(`config.efirma.flows.${flow}`)}
        </h3>
        <Badge variant={uploaded ? "success" : "secondary"}>
          {uploaded
            ? t("config.efirma.status.loaded")
            : t("config.efirma.status.empty")}
        </Badge>
      </div>

      {uploaded ? (
        <dl className="space-y-1 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <dt className="font-medium">{t("config.efirma.loaded")}</dt>
          </div>
          <div className="flex items-center gap-1">
            <dt className="font-medium">{t("config.efirma.updatedAt")}:</dt>
            <dd>{formatTimestamp(meta?.updatedAt)}</dd>
          </div>
        </dl>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t("config.efirma.notConfigured")}
        </p>
      )}

      {!editing ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setEditing(true)}
        >
          <Pencil className="size-4" />
          {t("config.efirma.replace")}
        </Button>
      ) : (
      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        <div className="space-y-1.5">
          <Label htmlFor={cerId}>{t("config.efirma.cerFile")}</Label>
          <input
            key={`cer-${resetToken}`}
            id={cerId}
            type="file"
            accept=".cer,.crt"
            className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-accent"
            {...form.register("cer")}
            aria-invalid={!!form.formState.errors.cer}
          />
          <p className="text-xs text-muted-foreground">
            {cerName ?? t("config.efirma.noFileSelected")}
          </p>
          {form.formState.errors.cer ? (
            <p className="text-xs text-destructive">
              {t(form.formState.errors.cer.message ?? "")}
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={keyId}>{t("config.efirma.keyFile")}</Label>
          <input
            key={`key-${resetToken}`}
            id={keyId}
            type="file"
            accept=".key"
            className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-accent"
            {...form.register("key")}
            aria-invalid={!!form.formState.errors.key}
          />
          <p className="text-xs text-muted-foreground">
            {keyName ?? t("config.efirma.noFileSelected")}
          </p>
          {form.formState.errors.key ? (
            <p className="text-xs text-destructive">
              {t(form.formState.errors.key.message ?? "")}
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={passwordId}>{t("config.efirma.password")}</Label>
          <Input
            id={passwordId}
            type="password"
            autoComplete="new-password"
            placeholder={t("config.efirma.passwordPlaceholder")}
            {...form.register("password")}
            aria-invalid={!!form.formState.errors.password}
          />
          {form.formState.errors.password ? (
            <p className="text-xs text-destructive">
              {t(form.formState.errors.password.message ?? "")}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <UploadCloud className="size-4" />
            )}
            {saveMutation.isPending
              ? t("config.efirma.uploading")
              : t("config.efirma.upload")}
          </Button>
          {uploaded ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={saveMutation.isPending}
              onClick={cancelEdit}
            >
              <X className="size-4" />
              {t("config.efirma.cancel")}
            </Button>
          ) : null}
        </div>
      </form>
      )}
    </div>
  );
}

function NumField({
  label,
  value,
  min,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs text-muted-foreground">{label}</span>
      <input
        type="number"
        min={min}
        value={value}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value) || 0))}
        className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
    </label>
  );
}
